import { ReviewsOutlined } from '@mui/icons-material';
import { Alert, alertClasses, AlertColor, Button, Slide, Stack } from '@mui/material';
import { Permission } from '@procureai/permissions';
import { Restricted } from '@procureai/permissions-ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { RouteId } from '@/legacy/routes';
import { useRuntime } from '~/components/RuntimeProvider';

import {
  useExtensionPurchaseReqNumberActorRef,
  useExtensionPurchaseReqNumberSelector,
} from './extensionPurchaseReqNumberActor';

const ExtensionPurchaseRequisitionNumberAlertComponent = () => {
  const { t } = useTranslation();

  const { send } = useExtensionPurchaseReqNumberActorRef();
  const isShowingDemand = useExtensionPurchaseReqNumberSelector((state) => state.matches('showingDemand'));
  const isShowingNoDemand = useExtensionPurchaseReqNumberSelector((state) => state.matches('showingNoDemand'));
  const purchaseReqNumber = useExtensionPurchaseReqNumberSelector((state) => state.context.purchaseReqNumber);
  const demandId = useExtensionPurchaseReqNumberSelector((state) => state.context.demandId);

  if (!purchaseReqNumber) return null;

  return (
    <>
      <Slide in={isShowingDemand && Boolean(demandId)}>
        <Alert
          severity="info"
          onClose={() => {
            send({ type: 'CLOSE_ALERT' });
          }}
          sx={(theme) => ({
            position: 'fixed',
            top: 0,
            right: 0,
            left: 0,
            zIndex: `${theme.zIndex.snackbar}`,
            [`& .${alertClasses.message}`]: {
              display: 'flex',
              alignItems: 'center',
              flex: '1 1',
              padding: 0,
            },
            borderRadius: 0,
          })}
        >
          <Stack direction="row" width="100%" display="flex" justifyContent="space-between" alignItems="center">
            {t('PurchaseRequisitionAlert.startConversationMessage', { purchaseReqNumber })}
            <Button
              component={Link}
              to={RouteId.cockpitConversationEntity('demand', demandId!)}
              variant="contained"
              color="primary"
              size="small"
              endIcon={<ReviewsOutlined />}
              onClick={() => {
                send({ type: 'NAVIGATE_TO_DEMAND' });
              }}
            >
              {t('PurchaseRequisitionAlert.chat')}
            </Button>
          </Stack>
        </Alert>
      </Slide>

      <Slide in={isShowingNoDemand}>
        <Alert
          severity="info"
          onClose={() => {
            send({ type: 'CLOSE_ALERT' });
          }}
          color={'grey.900' as AlertColor}
          sx={(theme) => ({
            position: 'fixed',
            top: 0,
            right: 0,
            left: 0,
            zIndex: `${theme.zIndex.snackbar}`,
            backgroundColor: `${theme.palette.grey[300]}`,
          })}
        >
          {t('PurchaseRequisitionAlert.noDemandMessage', { purchaseReqNumber })}
        </Alert>
      </Slide>
    </>
  );
};

export const ExtensionPurchaseRequisitionNumberAlert = () => {
  const { runtime } = useRuntime();

  if (runtime?.type !== 'browserExtension') {
    return null;
  }

  return (
    <Restricted needs={{ permission: Permission.ConversationDemandUse }}>
      <ExtensionPurchaseRequisitionNumberAlertComponent />
    </Restricted>
  );
};
